import React from 'react';
import { AssignmentModel } from 'api/model';
import { formatDate, formatDateTime } from 'utils/date-helpers';
import styled from 'styled-components';
import {
  useAssignmentAssignRegularAssignmentToCarTransportTruckAssignment,
  useAssignmentGetCarTransportTruckAssignmentsWithDetailsForDate,
} from 'api/assignment/assignment';
import TextButton from 'components/inputs/TextButton';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
// import Cars from 'components/icons/Cars';

const CarTransportTruckWrapper = styled.div<{
  selected?: boolean;
  isEligible?: boolean;
}>`
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.foreground.tint : theme.colors.border.primary};
  padding: 8px;
  margin: 8px 0;
  background-color: ${({ selected, theme }) =>
    selected
      ? theme.colors.background.secondary
      : theme.colors.background.primary};
  // hover effect
  ${({ isEligible, theme }) =>
    isEligible
      ? {
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: theme.colors.background.secondary,
          },
        }
      : {
          color: theme.colors.foreground.tertiary,
        }}
`;

// right aling button
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 5px;
`;

type Props = {
  assignment: AssignmentModel;
  onClose?: () => void;
};

const AssignToCarTransportTruckContent: React.FC<Props> = ({
  assignment,
  onClose,
}) => {
  // api requests
  const getRelevantTransportTruckAssignmentsCall =
    useAssignmentGetCarTransportTruckAssignmentsWithDetailsForDate({
      date: assignment.bookedTo,
    });

  const assignRegularAssignmentToCarTransportTruckAssignmentCall =
    useAssignmentAssignRegularAssignmentToCarTransportTruckAssignment();

  // TODO: assign Cars
  const [selected, setSelected] = React.useState<number | null>(null);

  const handleAssign = async () => {
    if (selected === null) {
      return;
    }

    try {
      await assignRegularAssignmentToCarTransportTruckAssignmentCall.mutateAsync(
        {
          params: {
            assignmentId: assignment.assignmentID,
            carTransportTruckAssignmentId: selected,
          },
        }
      );
      onClose?.();
    } catch (error) {
      showException(error);
    }
  };

  return (
    <div>
      <h3>
        Tillgängliga lastbilar från {assignment.fromName} till{' '}
        {assignment.toName} {formatDate(assignment.bookedTo)}
      </h3>
      {getRelevantTransportTruckAssignmentsCall.data?.map(
        (transportAssignment) => {
          // isEligible is a boolean that is true if the truck truck has the same from and to address and zip as the assignment
          const isEligible =
            transportAssignment.fromAddress === assignment.fromAddress &&
            transportAssignment.fromZip === assignment.fromZip &&
            transportAssignment.toAddress === assignment.toAddress &&
            transportAssignment.toZip === assignment.toZip;
          return (
            <CarTransportTruckWrapper
              isEligible={isEligible}
              key={transportAssignment.carTransportTruckAssignmentID}
              onClick={() => {
                if (isEligible) {
                  setSelected(
                    transportAssignment.carTransportTruckAssignmentID
                  );
                } else {
                  setSelected(null);
                }
              }}
              selected={
                selected === transportAssignment.carTransportTruckAssignmentID
              }
            >
              <div>
                <strong>
                  {`${transportAssignment.carTransportTruckRegistrationNumber} (${transportAssignment.maxNumberOfCars - transportAssignment.transportedAssignments.length} av ${transportAssignment.maxNumberOfCars ?? '0'} platser lediga)`}
                </strong>
              </div>
              <div>
                Tilldelade bilar:{' '}
                {transportAssignment.transportedAssignments.length > 0
                  ? transportAssignment.transportedAssignments.map((a) => (
                      <TextButton key={a.assignmentID}>
                        {a.caseRegistrationNumber}{' '}
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </TextButton>
                    ))
                  : '–'}
              </div>
              <div>
                Estimerad starttid:{' '}
                {transportAssignment.estimatedStartTime
                  ? formatDateTime(transportAssignment.estimatedStartTime)
                  : '-'}
              </div>
              <div>
                Bokat till:{' '}
                {transportAssignment.bookedTo
                  ? formatDateTime(transportAssignment.bookedTo)
                  : '-'}
              </div>
              {!isEligible && (
                <div>
                  <span style={{ color: 'red', fontSize: '0.8em' }}>
                    Lastbilen har inte samma adress och postnummer som uppdraget
                  </span>
                </div>
              )}
            </CarTransportTruckWrapper>
          );
        }
      )}
      <ButtonWrapper>
        <Button onClick={onClose}>Avbryt</Button>
        <Button
          onClick={handleAssign}
          loading={
            assignRegularAssignmentToCarTransportTruckAssignmentCall.isLoading
          }
        >
          Tilldela
        </Button>
      </ButtonWrapper>
    </div>
  );
};

export default AssignToCarTransportTruckContent;
